import { ESubStep } from "common/enums/Subscription/ESubStep";
import { ValidationError } from "common/resources/Resource";
import Button, { EButtonVariant } from "components/elements/Button";
import Modal from "components/elements/Modal";
import Typography, { ITypo, ITypoComponent } from "components/elements/Typography";
import DatePickerInputElement from "components/materials/Form/DatePickerInputElement";
import Form from "components/materials/Form";
import MuiSelectInputElement, { IMuiSelectOption } from "components/materials/Form/MuiSelectInputElement";
import { useCallback, useMemo, useState } from "react";

import classes from "./classes.module.scss";

type IProps = {
	isOpen: boolean;
	onClose: () => void;
	onSave: (status: ESubStep, validationDate?: Date) => void;
	isSubmitting?: boolean;
};

const statusOptions: IMuiSelectOption[] = [
	{ id: ESubStep.processing, label: "Statut À traiter" },
	{ id: ESubStep.validated, label: "Statut Validé (sans email au cgp)" },
];

export default function StatusSelectionModal(props: IProps) {
	const { isOpen, onClose, onSave, isSubmitting = false } = props;
	const [selectedStatus, setSelectedStatus] = useState<ESubStep>(ESubStep.processing);
	const [validationDate, setValidationDate] = useState<Date | null>(new Date());
	const [errors, setErrors] = useState<ValidationError[]>([]);

	const defaultStatusOption = useMemo(() => statusOptions.find((opt) => opt.id === selectedStatus) || statusOptions[0], [selectedStatus]);

	const handleStatusChange = useCallback((_event: React.SyntheticEvent, value: IMuiSelectOption | null) => {
		if (value) {
			setSelectedStatus(value.id as ESubStep);
		}
	}, []);

	const handleSubmit = useCallback(
		(e: React.FormEvent<HTMLFormElement>, formData: { [key: string]: unknown }) => {
			e.preventDefault();
			if (isSubmitting) return;
			setErrors([]);

			const status = selectedStatus;
			const date = status === ESubStep.validated ? (formData["validationDate"] ? new Date(formData["validationDate"] as string) : new Date()) : undefined;

			onSave(status, date);
		},
		[selectedStatus, onSave, isSubmitting],
	);

	const isValidationDateRequired = selectedStatus === ESubStep.validated;
	const isSaveDisabled = isSubmitting || (isValidationDateRequired && !validationDate);

	return (
		<Modal isOpen={isOpen} onClose={isSubmitting ? undefined : onClose}>
			<div className={classes["root"]}>
				<Typography typo={ITypo.H1} component={ITypoComponent.H2}>
					Choisir le statut de la souscription
				</Typography>
				<Typography typo={ITypo.P_MEDIUM_BOLD}>Sélectionnez le statut final de la souscription</Typography>
				<Form className={classes["form"]} onSubmit={handleSubmit} errors={errors}>
					<MuiSelectInputElement name="status" label="Statut" options={statusOptions} defaultValue={defaultStatusOption} onChange={handleStatusChange} disableClearable />

					{isValidationDateRequired && (
						<DatePickerInputElement
							name="validationDate"
							label="Date de validation"
							defaultValue={validationDate || new Date()}
							onChange={(date) => setValidationDate(date)}
						/>
					)}

					<div className={classes["button-container"]}>
						<Button fullwidth variant={EButtonVariant.OUTLINED} onClick={onClose} disabled={isSubmitting}>
							Annuler
						</Button>

						<Button fullwidth type="submit" disabled={isSaveDisabled}>
							{isSubmitting ? "Enregistrement…" : "Enregistrer"}
						</Button>
					</div>
				</Form>
			</div>
		</Modal>
	);
}
